import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import HTMLDisplay from "../../../utils/HTMLDisplay";
import axios from "axios";
import "../../../assets/css/LatestNewSection.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BASE_URL = process.env.REACT_APP_API_BASE_URL;

function formatNewsDate(value) {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function hasNewsImage(item) {
  return (
    typeof item?.passport === "string" &&
    item.passport.trim() !== "" &&
    item.passport !== "undefined" &&
    item.passport !== "null"
  );
}

function LatestNewSection({ datacontent = [], initialPagination, id }) {
  const [newsList, setNewsList] = useState(datacontent || []);
  const [selectedNews, setSelectedNews] = useState(datacontent?.[0] || null);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(
    initialPagination || { current_page: 1, last_page: 1 },
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const fetchNews = useCallback(async () => {
    setLoading(true);
    setError(false);

    try {
      const response = await axios.get(
        `${BASE_URL}posdata?pageName[eq]=${id}&columes[eq]=title_show_echo_01zz&ministry[eq]=1&status02[eq]=publish&status[eq]=Public&visibility[eq]=Public&page=${page}&per_page=4`,
      );

      const allData = response?.data?.allData || [];

      setNewsList(allData);
      setSelectedNews(allData[0] || null);
      setPagination(
        response?.data?.pagination || {
          current_page: page,
          last_page: 1,
        },
      );
    } catch (err) {
      console.error("Unable to load latest news:", err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [id, page]);

  useEffect(() => {
    fetchNews();
  }, [fetchNews]);

  useEffect(() => {
    if (datacontent?.length && page === 1) {
      setNewsList(datacontent);
      setSelectedNews(datacontent[0]);
    }
  }, [datacontent, page]);

  const currentPage = Number(pagination?.current_page || page);
  const lastPage = Math.max(1, Number(pagination?.last_page || 1));

  const goToPage = (nextPage) => {
    if (nextPage < 1 || nextPage > lastPage || nextPage === page) return;
    setPage(nextPage);
  };

  if (loading && newsList.length === 0) {
    return (
      <section id="latest-news" className="fmhud-latest section">
        <div className="container">
          <div className="fmhud-latest-status">Loading latest news...</div>
        </div>
      </section>
    );
  }

  if (error && newsList.length === 0) {
    return (
      <section id="latest-news" className="fmhud-latest section">
        <div className="container">
          <div className="fmhud-latest-status" role="alert">
            <p>Latest news could not be loaded.</p>
            <button type="button" onClick={fetchNews}>
              Try Again
            </button>
          </div>
        </div>
      </section>
    );
  }

  if (!selectedNews) return null;

  const imageUrl = hasNewsImage(selectedNews)
    ? `${BACKEND_URL}upload/${selectedNews.passport}`
    : null;

  const otherStories = newsList.filter((item) => item.id !== selectedNews.id);

  return (
    <section id="latest-news" className="fmhud-latest section">
      <div className="container">
        {/* Section heading */}
        <div className="fmhud-news-heading">
          <div className="fmhud-news-heading-copy">
            <span className="fmhud-news-eyebrow">NEWS &amp; UPDATES</span>

            <h2>LATEST NEWS</h2>
            {/* 
            <p>
              Discover the latest news, activities and developments
              from the Federal Ministry of Housing and Urban Development.
            </p> */}
          </div>
          <div className="tm-modern-heading-mark" aria-hidden="true">
            <i className="fa-regular fa-newspaper" />
          </div>
        </div>
        {loading && (
          <div className="fmhud-latest-loading" role="status">
            Updating news...
          </div>
        )}

        {error && (
          <div className="fmhud-latest-inline-error" role="alert">
            We could not refresh the news. Previously loaded stories remain
            available.
          </div>
        )}

        {/* Editorial layout */}
        <div className="fmhud-latest-layout">
          {/* Featured story */}
          <article className="fmhud-featured-story">
            <Link
              to={`/read/${selectedNews.id}`}
              className="fmhud-featured-image"
              aria-label="Read featured story"
            >
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={selectedNews.title || "Featured ministry news"}
                  loading="lazy"
                  decoding="async"
                />
              ) : (
                <div className="fmhud-featured-placeholder">
                  <span>fmhud</span>
                  <small>OFFICIAL NEWS</small>
                </div>
              )}

              <span className="fmhud-featured-image-label">FEATURED STORY</span>
            </Link>

            <div className="fmhud-featured-content">
              <div className="fmhud-featured-meta">
                <span className="fmhud-news-category">Latest News</span>
                {selectedNews.daterqw && (
                  <span>
                    <i className="fa-regular fa-calendar" />{" "}
                    {formatNewsDate(selectedNews.daterqw)}
                  </span>
                )}
              </div>

              <h3>
                <HTMLDisplay
                  html={selectedNews.story01 || selectedNews.title || ""}
                  textNo={22}
                  type="title"
                />
              </h3>

              <div className="fmhud-featured-summary">
                <HTMLDisplay
                  html={selectedNews.story01 || ""}
                  textNo={35}
                  type="body"
                />
              </div>

              <Link
                to={`/read/${selectedNews.id}`}
                className="fmhud-featured-link"
              >
                Read Full Story
                <i className="fa-solid fa-arrow-right" />
              </Link>
            </div>
          </article>

          {/* Other stories */}
          <aside className="fmhud-latest-sidebar">
            <div className="fmhud-sidebar-heading">
              <h3>More Headlines</h3>
              <span>{otherStories.length} stories</span>
            </div>

            {otherStories.length > 0 ? (
              <div className="fmhud-headline-list">
                {otherStories.map((item) => {
                  const thumbnail = hasNewsImage(item)
                    ? `${BACKEND_URL}upload/${item.passport}`
                    : null;

                  return (
                    <article className="fmhud-headline-item" key={item.id}>
                      <button
                        type="button"
                        className="fmhud-headline-select"
                        onClick={() => setSelectedNews(item)}
                        aria-label={`Feature ${
                          item.title || "this news story"
                        }`}
                      >
                        <span className="fmhud-headline-thumbnail">
                          {thumbnail ? (
                            <img
                              src={thumbnail}
                              alt=""
                              loading="lazy"
                              decoding="async"
                            />
                          ) : (
                            <span className="fmhud-thumbnail-placeholder">
                              fmhud
                            </span>
                          )}
                        </span>

                        <span className="fmhud-headline-details">
                          <span className="fmhud-headline-date">
                            {formatNewsDate(item.daterqw)}
                          </span>

                          <span className="fmhud-headline-title">
                            <HTMLDisplay
                              html={item.story01 || item.title || ""}
                              textNo={8}
                              type="title"
                            />
                          </span>

                          <span className="fmhud-headline-action">
                            View story <span aria-hidden="true">→</span>
                          </span>
                        </span>
                      </button>
                    </article>
                  );
                })}
              </div>
            ) : (
              <p className="fmhud-no-headlines">
                No other stories are available on this page.
              </p>
            )}

            <div className="fmhud-latest-pagination">
              <span>
                Page <strong>{currentPage}</strong> of{" "}
                <strong>{lastPage}</strong>
              </span>

              <div className="fmhud-pagination-buttons">
                <button
                  type="button"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage <= 1 || loading}
                  aria-label="Previous page"
                >
                  <i className="fa-solid fa-arrow-left" />
                </button>

                <button
                  type="button"
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage >= lastPage || loading}
                  aria-label="Next page"
                >
                  <i className="fa-solid fa-arrow-right" />
                </button>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}

export default React.memo(LatestNewSection);
